import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './DataPage.css';
import Pagination from '../components/Pagination';

import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';

function Solicitacoes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pacienteIdFromUrl = searchParams.get('pacienteId');
  const medicoIdFromUrl = searchParams.get('medicoId');

  // Calcular data padrão (últimos 7 dias) - mas não usar se vier com pacienteId ou medicoId
  const hoje = new Date();
  const seteDiasAtras = new Date();
  seteDiasAtras.setDate(hoje.getDate() - 7);

  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [dataInicio, setDataInicio] = useState((pacienteIdFromUrl || medicoIdFromUrl) ? '' : seteDiasAtras.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState((pacienteIdFromUrl || medicoIdFromUrl) ? '' : hoje.toISOString().split('T')[0]);
  const [pacienteId, setPacienteId] = useState(pacienteIdFromUrl || '');
  const [medicoId, setMedicoId] = useState(medicoIdFromUrl || '');
  const [medicoNome, setMedicoNome] = useState('');
  const [sexo, setSexo] = useState('');
  const [usuario, setUsuario] = useState('');
  const [idadeMin, setIdadeMin] = useState('');
  const [idadeMax, setIdadeMax] = useState('');
  const [sortBy, setSortBy] = useState('DATA');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [resumo, setResumo] = useState({
    total: 0,
    pacientesUnicos: 0,
    masculino: 0,
    feminino: 0,
    hoje: 0
  });

  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    if (medicoId) {
      fetchMedicoNome();
    }
  }, [medicoId]);

  // Auto-fetch on pagination/sort changes and manual triggers (also handles initial mount)
  useEffect(() => {
    fetchSolicitacoes();
  }, [currentPage, itemsPerPage, sortBy, sortOrder, fetchTrigger]);

  const handleSearch = () => {
    if (currentPage === 1) {
      setFetchTrigger(prev => prev + 1);
    } else {
      setCurrentPage(1);
    }
  };

  const fetchMedicoNome = async () => {
    try {
      const response = await axios.get(`${API_URL}/medicos/${medicoId}`);
      setMedicoNome(response.data.NOME || '');
    } catch (err) {
      console.error('Erro ao buscar médico:', err);
    }
  };

  const fetchSolicitacoes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder
      });

      if (searchInput) params.append('search', searchInput);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (pacienteId) params.append('pacienteId', pacienteId);
      if (medicoId) params.append('medicoId', medicoId);
      if (sexo) params.append('sexo', sexo);
      if (usuario) params.append('usuario', usuario);
      if (idadeMin) params.append('idadeMin', idadeMin);
      if (idadeMax) params.append('idadeMax', idadeMax);

      const response = await axios.get(`${API_URL}/solicitacoes?${params.toString()}`);
      const { data, total } = response.data;

      setSolicitacoes(data);
      setTotalItems(total);

      setResumo(response.data.totais || { total: 0, pacientesUnicos: 0, masculino: 0, feminino: 0, hoje: 0 });
    } catch (err) {
      console.error('Erro ao buscar solicitações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  // formatDate importado de ../utils/formatDate

  const clearFilters = () => {
    setSearchInput('');
    setPacienteId('');
    setMedicoId('');
    setMedicoNome('');
    setSexo('');
    setUsuario('');
    setIdadeMin('');
    setIdadeMax('');
    navigate('/solicitacoes'); // Remove URL params
    const hoje = new Date();
    const seteDiasAtras = new Date();
    seteDiasAtras.setDate(hoje.getDate() - 7);
    setDataInicio(seteDiasAtras.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  if (loading && solicitacoes.length === 0) {
    return <div className="loading">Carregando solicitações...</div>;
  }

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
    btnPesquisar: {
      padding: '8px 20px',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '600',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div className="data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>📋</span>
          <div>
            <h1 style={styles.pageTitle}>Solicitações</h1>
            <p style={styles.pageSubtitle}>
              Exibindo {resumo.total} de {totalItems} solicitações
              {(searchInput || dataInicio || dataFim || pacienteId || medicoId) && ' (filtrado)'}
            </p>
          </div>
        </div>
      </div>

      {pacienteId && (
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
        }}>
          <div>
            <strong>👤 Filtrando por Paciente ID: {pacienteId}</strong>
          </div>
          <button
            onClick={clearFilters}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕ Remover Filtro
          </button>
        </div>
      )}

      {medicoId && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
        }}>
          <div>
            <strong>👨‍⚕️ Filtrando por Médico: {medicoNome || `ID ${medicoId}`}</strong>
          </div>
          <button
            onClick={clearFilters}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕ Remover Filtro
          </button>
        </div>
      )}

      <div className="filters-section">
        <div className="search-bar" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por protocolo, nome do paciente ou CPF... (pressione Enter)"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              // Remove filtros de data quando buscar por nome/protocolo/CPF
              if (e.target.value) {
                setDataInicio('');
                setDataFim('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className="search-input"
          />
          <button onClick={handleSearch} style={styles.btnPesquisar}>
            Pesquisar
          </button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Sexo:</label>
            <select
              value={sexo}
              onChange={(e) => setSexo(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Usuário:</label>
            <input
              type="text"
              placeholder="Nome do usuário..."
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Idade Mín:</label>
            <input
              type="number"
              placeholder="Ex: 18"
              value={idadeMin}
              onChange={(e) => setIdadeMin(e.target.value)}
              className="filter-select"
              min="0"
              max="120"
            />
          </div>

          <div className="filter-group">
            <label>Idade Máx:</label>
            <input
              type="number"
              placeholder="Ex: 65"
              value={idadeMax}
              onChange={(e) => setIdadeMax(e.target.value)}
              className="filter-select"
              min="0"
              max="120"
            />
          </div>

          <div className="filter-group">
            <label>Por página:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="filter-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {(searchInput || sexo || usuario || idadeMin || idadeMax) && (
            <button onClick={clearFilters} className="clear-filters-btn">
              🔄 Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Resumo dos Dados Filtrados */}
      <div className="compact-stats">
        <div className="compact-stat-card purple">
          <div className="compact-stat-value">{(resumo.total || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Solicitações</div>
        </div>
        <div className="compact-stat-card blue">
          <div className="compact-stat-value">{(resumo.pacientesUnicos || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Pacientes Únicos</div>
        </div>
        <div className="compact-stat-card cyan">
          <div className="compact-stat-value">{(resumo.masculino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Masculino</div>
        </div>
        <div className="compact-stat-card pink">
          <div className="compact-stat-value">{(resumo.feminino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Feminino</div>
        </div>
        <div className="compact-stat-card green">
          <div className="compact-stat-value">{(resumo.hoje || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Hoje</div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-responsive">
          <table className="data-table compact-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('PROTOCOLO')} style={{ cursor: 'pointer', width: '120px' }}>
                  Protocolo {getSortIcon('PROTOCOLO')}
                </th>
                <th onClick={() => handleSort('PACIENTE_NOME')} style={{ cursor: 'pointer' }}>
                  Paciente {getSortIcon('PACIENTE_NOME')}
                </th>
                <th onClick={() => handleSort('DATA')} style={{ cursor: 'pointer', width: '140px' }}>
                  Data {getSortIcon('DATA')}
                </th>
                <th style={{ width: '140px' }}>Usuário</th>
              </tr>
            </thead>
            <tbody>
              {solicitacoes.map((sol) => (
                <React.Fragment key={sol.ID}>
                  <tr
                    onClick={() => navigate(`/protocolo/${sol.PROTOCOLO}`)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <strong style={{ fontSize: '1.1rem', color: '#3b82f6' }}>{sol.PROTOCOLO}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>ID: {sol.ID}</div>
                    </td>
                    <td>
                      <strong style={{ fontSize: '1.05rem', color: '#1f2937' }}>{sol.PACIENTE_NOME || '-'}</strong>
                      <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '0.9rem' }}>
                        CPF: {sol.PACIENTE_CPF || '-'}
                      </div>
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                      <div style={{ fontWeight: '600' }}>{formatDate(sol.DATA)}</div>
                      {sol.HORARIO && (
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                          {sol.HORARIO}
                        </div>
                      )}
                      {sol.DATAENTREGA && (
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                          Entrega: {formatDate(sol.DATAENTREGA)}
                        </div>
                      )}
                    </td>
                    <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px', fontSize: '0.9rem' }}>
                      {sol.USUARIO || '-'}
                    </td>
                  </tr>
                  <tr onClick={() => navigate(`/protocolo/${sol.PROTOCOLO}`)} style={{ cursor: 'pointer' }} className="clickable-row">
                    <td style={{ borderTop: 'none', paddingTop: '0', color: '#6b7280', fontSize: '0.9rem' }}>
                      {sol.PACIENTE_SEXO === 'M' ? '👨' : sol.PACIENTE_SEXO === 'F' ? '👩' : '👤'} {sol.PACIENTE_SEXO === 'M' ? 'Masculino' : sol.PACIENTE_SEXO === 'F' ? 'Feminino' : 'Não informado'}
                      {sol.IDADE_PACIENTE && ` • Idade: ${sol.IDADE_PACIENTE} anos`}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />
    </div>
  );
}

export default Solicitacoes;
